import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquare, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Compact "Comments Sync Health" tile for the Migration Board header.
 *
 * Answers "are notes and stage moves reliably making it into Syncro?" — a
 * silent-fail regression like the one behind v1.10.82 (no comments were
 * flowing because 60/66 cards lacked a ``syncro_ticket_id`` and the mirror
 * short-circuited without logging) would surface here within an hour of
 * happening instead of hiding until a customer noticed.
 *
 * Reads ``/commissions/migrations/comments-health``. Filters by
 * ``partner_code`` when the board is scoped to one partner.
 */
export default function CommentsSyncHealthTile({ partnerCode }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const params = partnerCode ? { partner_code: partnerCode } : {};
        const { data } = await api.get("/commissions/migrations/comments-health", { params });
        if (alive) setData(data);
      } catch {
        if (alive) setData({ error: true });
      }
    })();
    return () => { alive = false; };
  }, [partnerCode]);

  if (data === null) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-xs text-slate-500 inline-flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading sync health…
      </div>
    );
  }
  if (data.error) return null;

  const successPct = data.success_pct;
  const verdict = data.primary_events === 0 ? "idle"
                : successPct == null ? "unknown"
                : successPct >= 95 ? "healthy"
                : successPct >= 80 ? "degraded"
                : "unhealthy";
  const cfg = {
    idle:      { color: "text-slate-400",   bg: "bg-slate-800/50",  border: "border-slate-700",   label: "No activity" },
    unknown:   { color: "text-slate-300",   bg: "bg-slate-800/60",  border: "border-slate-700",   label: "—" },
    healthy:   { color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", label: "Healthy" },
    degraded:  { color: "text-amber-300",   bg: "bg-amber-500/10",   border: "border-amber-500/40",   label: "Degraded" },
    unhealthy: { color: "text-red-300",     bg: "bg-red-500/10",     border: "border-red-500/40",     label: "Unhealthy" },
  }[verdict];

  const Icon = verdict === "healthy" || verdict === "idle" ? CheckCircle2 : AlertTriangle;

  const detailBits = [];
  if (data.mirror_ok > 0) detailBits.push(`${data.mirror_ok} synced`);
  if (data.mirror_failed > 0) detailBits.push(`${data.mirror_failed} failed`);
  if (data.silent_skips > 0) detailBits.push(`${data.silent_skips} skipped`);
  const detail = detailBits.length ? detailBits.join(" · ") : "no comments in 30d";

  const failure = data.latest_failures?.[0];

  return (
    <div
      data-testid="comments-sync-health-tile"
      className={`inline-flex flex-col gap-1 rounded-lg border ${cfg.border} ${cfg.bg} px-3 py-2 min-w-[220px]`}
      title={failure ? `Latest failure: ${failure.business_name} — ${failure.details?.reason}` : ""}
    >
      <div className="flex items-center gap-2">
        <MessageSquare className={`w-3.5 h-3.5 ${cfg.color}`} />
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Comments → Syncro</span>
        <span className={`ml-auto inline-flex items-center gap-1 text-[10px] font-bold ${cfg.color}`} data-testid="comments-sync-verdict">
          <Icon className="w-3 h-3" />
          {successPct != null ? `${successPct}%` : cfg.label}
        </span>
      </div>
      <div className="text-[11px] text-slate-400" data-testid="comments-sync-detail">
        Last 30d: {detail}
      </div>
      {failure && (
        <div className="text-[10px] text-red-300/80 truncate" title={failure.business_name}>
          ↳ {failure.business_name} · {failure.details?.reason || "unknown"}
        </div>
      )}
    </div>
  );
}
